import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Divider,
  Fade,
  Tooltip,
  Alert,
  Snackbar,
  Badge
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Refresh as RefreshIcon,
  ErrorOutline,
  Info as InfoIcon,
  QuestionAnswer,
  Search,
  Notifications
} from '@mui/icons-material';

const NotificationDialog = ({ open, onClose, notifications, markAsRead, fetchNotifications, currentUser }) => {
  // 分類標籤：全部 / 未讀 / 未回覆
  const [tabFilter, setTabFilter] = useState('all');
  // 時間與團號查詢條件
  const [filterYear, setFilterYear] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const itemsPerPage = 10; // 每頁顯示通知數量
  
  // 載入狀態管理
  const [isLoading, setIsLoading] = useState(false); 
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // 通知訊息
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');
  const [snackOpen, setSnackOpen] = useState(false);

  useEffect(() => {
    // 當使用者切換 tabFilter 時，自動重設 page
    setPage(1);
  }, [tabFilter]);

  // 重新整理通知的函數
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return; // 防止重複請求
    
    setIsRefreshing(true);
    
    try {
      await fetchNotifications();
      setSnackMessage('通知已刷新');
      setSnackSeverity('success');
      setSnackOpen(true);
    } catch (error) {
      console.error('獲取通知時發生錯誤:', error);
      setSnackMessage('獲取通知失敗，請稍後再試');
      setSnackSeverity('error');
      setSnackOpen(true);
    } finally {
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500); // 給使用者一個明顯的視覺反饋
    }
  }, [fetchNotifications, isRefreshing]);

  useEffect(() => {
    // 當對話框被打開時，觸發一次重新整理
    if (open && !notifications) {
      handleRefresh();
    }
  }, [open, notifications, handleRefresh]);

  // 使用 useMemo 優化篩選邏輯，避免重複計算
  const filtered = useMemo(() => {
    let result = notifications || [];
    
    // 根據年份、月份過濾
    if (filterYear) {
      result = result.filter(notif => {
        const date = new Date(notif.created_at);
        return date.getFullYear().toString() === filterYear.toString();
      });
    }
    
    if (filterMonth) {
      result = result.filter(notif => {
        const date = new Date(notif.created_at);
        return (date.getMonth() + 1).toString() === filterMonth.toString();
      });
    }
    
    // 根據團號搜尋（忽略大小寫）
    if (searchTerm) {
      result = result.filter(notif =>
        notif.order_number?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return result;
  }, [notifications, filterYear, filterMonth, searchTerm]);
  
  // 根據分類標籤進一步過濾
  const displayedNotifications = useMemo(() => {
    if (tabFilter === 'unread') {
      return filtered.filter(notif => !notif.is_read);
    } else if (tabFilter === 'unreplied') {
      return filtered.filter(notif => notif.is_read && !notif.is_reply);
    }
    return filtered;
  }, [filtered, tabFilter]);
  
  // 計算每個分類的數量，用於更新 Badge
  const counts = useMemo(() => ({
    all: filtered.length,
    unread: filtered.filter(n => !n.is_read).length,
    unreplied: filtered.filter(n => n.is_read && !n.is_reply).length
  }), [filtered]);

  // 提取所有年份（從新到舊排序）
  const years = useMemo(() => notifications ? Array.from(
    new Set(notifications.map(notif => new Date(notif.created_at).getFullYear()))
  ).sort((a, b) => b - a) : [], [notifications]);

  // 當有選年份時，提取該年份中所有月份（從小到大排序）
  const months = useMemo(() => filterYear && notifications
    ? Array.from(
        new Set(
          notifications
            .filter(notif => new Date(notif.created_at).getFullYear().toString() === filterYear.toString())
            .map(notif => new Date(notif.created_at).getMonth() + 1)
        )
      ).sort((a, b) => a - b)
    : [], [notifications, filterYear]);

  // 點擊通知後，先標記為已讀，再依據通知屬性開啟對應頁面
  const handleNotificationClick = async (notif) => {
    setIsLoading(true);
    try {
      if (!notif.is_read) {
        await markAsRead(notif.id);
      }
      let url = `/Japan/order-information/${notif.order_uuid}`;
      
      // 若需要定位到特定交流區塊，可以添加錨點
      if (notif.external_communication_id) {
        url += '#communications=external';
      } else if (notif.internal_communication_id) {
        url += '#communications=internal';
      }
      
      window.open(url, '_blank');
      setSnackMessage(notif.order_number + ' 訂單頁面已在新視窗開啟');
      setSnackSeverity('info');
      setSnackOpen(true);
      await fetchNotifications(); // 重新載入通知資料
    } catch (error) {
      console.error('處理通知時發生錯誤:', error);
      setSnackMessage('處理通知失敗，請稍後再試');
      setSnackSeverity('error');
      setSnackOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // 重置查詢條件
  const handleResetFilters = () => {
    setFilterYear('');
    setFilterMonth('');
    setSearchTerm('');
    setPage(1); // 重置回第一頁
    
    setSnackMessage('已重置所有過濾條件');
    setSnackSeverity('info');
    setSnackOpen(true);
  };

  // 取得當前頁面需要渲染的筆數
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const pagedNotifications = displayedNotifications.slice(startIndex, endIndex);

  // 自動調整頁數，避免篩選後顯示空白頁
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(displayedNotifications.length / itemsPerPage));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [displayedNotifications.length, page, itemsPerPage]);

  // 格式化日期顯示
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return `今天 ${date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `昨天 ${date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`;
    } else {
      return `${date.toLocaleDateString()}`;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          // 僅允許透過點擊關閉按鈕關閉，不接受 backdrop 或 Esc
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          onClose();
        }}
        disableEscapeKeyDown
        fullWidth
        maxWidth="sm"
        PaperProps={{ 
          style: { 
            height: '80vh', 
            borderRadius: '12px', 
            overflow: 'hidden',
            maxHeight: '700px'
          } 
        }}
      >
        <DialogTitle
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '4px 16px'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <Notifications sx={{ mr: 1 }} /> 通知中心
            {isRefreshing && (
              <CircularProgress size={20} color="inherit" sx={{ ml: 2 }} />
            )}
          </Typography>
          <Box>
            <Tooltip title="重新整理">
              <IconButton
                onClick={handleRefresh}
                disabled={isRefreshing}
                sx={{ color: '#fff', mr: 1 }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton aria-label="close" onClick={onClose} sx={{ color: '#fff' }} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#f9f9f9',
            position: 'relative'
          }}
        >
          {/* 查詢篩選區 */}
          <Box sx={{ display: 'flex', mt: 1, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <FormControl variant="outlined" size="small" sx={{ minWidth: 90 }}>
              <InputLabel>年份</InputLabel>
              <Select
                label="年份"
                value={filterYear}
                onChange={(e) => {
                  setFilterYear(e.target.value);
                  setFilterMonth('');
                  setPage(1);
                }}
              >
                <MenuItem value="">全部</MenuItem>
                {years.map(year => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" sx={{ minWidth: 90 }} disabled={!filterYear}>
              <InputLabel>月份</InputLabel>
              <Select
                label="月份"
                value={filterMonth}
                onChange={(e) => {
                  setFilterMonth(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">全部</MenuItem>
                {months.map(month => (
                  <MenuItem key={month} value={month}>
                    {month}月
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ flexGrow: 1, minWidth: 120 }}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="搜尋團號..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: <Search fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
                }}
              />
            </FormControl>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleResetFilters}
              disabled={!filterYear && !filterMonth && !searchTerm}
            >
              重置
            </Button>
          </Box>
          
          {/* 分類標籤 - 更新 Badge 計數 */}
          <Tabs
            value={tabFilter}
            onChange={(e, newValue) => setTabFilter(newValue)}
            variant="fullWidth"
            sx={{
              mb: '4px',
              '.MuiTabs-indicator': { backgroundColor: '#764ba2' },
              '.MuiTab-root': { textTransform: 'none', fontWeight: 'bold' }
            }}
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon fontSize="small" sx={{ mr: 0.5 }} />
                  所有通知
                  <Badge 
                    badgeContent={counts.all}
                    color="primary"
                    sx={{ ml: 3 }}
                  />
                </Box>
              } 
              value="all" 
              sx={{ p: 1 }}
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ErrorOutline fontSize="small" sx={{ mr: 0.5 }} />
                  未讀通知
                  <Badge 
                    badgeContent={counts.unread} 
                    color="error"
                    sx={{ ml: 3 }}
                  />
                </Box>
              } 
              value="unread" 
              sx={{ p: 1 }} 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <QuestionAnswer fontSize="small" sx={{ mr: 0.5 }} />
                  未回覆通知
                  <Badge 
                    badgeContent={counts.unreplied} 
                    color="primary"
                    sx={{ ml: 3 }}
                  />
                </Box>
              } 
              value="unreplied" 
              sx={{ p: 1 }} 
            />
          </Tabs>
          
          {/* 通知列表 - 簡化版，只顯示團號 */}
          <Box 
            sx={{ 
              flexGrow: 1, 
              overflowY: 'auto', 
              pr: 1, 
              mt: 1,
              position: 'relative',
              minHeight: '300px'
            }}
          >
            {/* 載入中覆蓋層 */}
            <Fade in={isLoading}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 2,
                  backdropFilter: 'blur(2px)'
                }}
              >
                <CircularProgress size={40} />
              </Box>
            </Fade>
            
            {!notifications ? (
              // 初始載入中
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress size={30} />
              </Box>
            ) : displayedNotifications.length === 0 ? (
              // 無通知資料
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100%',
                opacity: 0.7
              }}>
                <InfoIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="textSecondary" align="center">
                  沒有符合條件的通知
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
                  {searchTerm || filterYear || filterMonth ? '嘗試調整搜尋條件或重設過濾器' : '目前沒有任何通知'}
                </Typography>
              </Box>
            ) : (
              // 簡化顯示：只顯示團號列表
              <Box>
                {pagedNotifications.map((notif, idx) => (
                  <React.Fragment key={notif.id || idx}>
                    <Box
                      sx={{
                        p: 1,
                        cursor: 'pointer',
                        borderRadius: '4px',
                        mb: 0.5,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: notif.is_read ? 'background.paper' : 'rgba(118, 75, 162, 0.1)',
                        border: '1px solid #e0e0e0',
                        transition: 'all 0.2s ease',
                        '&:hover': { 
                          boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                          backgroundColor: '#f0f0f0'
                        }
                      }}
                      onClick={() => handleNotificationClick(notif)}
                    >
                      {/* 左側：團號和標記 */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {!notif.is_read && (
                          <Badge 
                            color="error" 
                            variant="dot" 
                            sx={{ mr: 1 }} 
                          />
                        )}
                        <Typography sx={{ fontWeight: notif.is_read ? 'normal' : 'bold' }}>
                          團號: {notif.order_number || 'N/A'}
                        </Typography>
                      </Box>
                      
                      {/* 右側：日期 */}
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(notif.created_at)}
                      </Typography>
                    </Box>
                    {idx < pagedNotifications.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </Box>
            )}
          </Box>
          
          {/* 分頁器 */}
          {notifications && displayedNotifications.length > 0 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mt: 2,
              pt: 1,
              borderTop: '1px solid #e0e0e0'
            }}>
              <Typography variant="body2" color="textSecondary">
                共 {displayedNotifications.length} 則通知
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={page === 1}
                  onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                  sx={{ minWidth: '80px' }}
                >
                  上一頁
                </Button>
                <Box sx={{ mx: 2, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2">
                    {page} / {Math.max(1, Math.ceil(displayedNotifications.length / itemsPerPage))}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={page >= Math.ceil(displayedNotifications.length / itemsPerPage)}
                  onClick={() => setPage(prev => Math.min(prev + 1, Math.ceil(displayedNotifications.length / itemsPerPage)))}
                  sx={{ minWidth: '80px' }}
                >
                  下一頁
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* 操作結果通知 */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackOpen(false)} 
          severity={snackSeverity} 
          variant="filled"
          elevation={6}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotificationDialog;
